import React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";

/**
 * Static table used inside report chart sections, where the payload is already
 * loaded and no further fetching or pagination is needed.
 *
 * @param {Array} columns - [{ field, headerName, width, align, renderCell }]
 * @param {Array} rows - Data rows
 */
const ReportTable = ({
  title,
  subtitle,
  columns = [],
  rows = [],
  emptyMessage = "No data available for the selected filters",
  maxHeight = 420,
}) => {
  const theme = useTheme();

  return (
    <Paper
      sx={{
        p: { xs: 1.5, sm: 2.5 },
        bgcolor: theme.palette.background.alt,
        border: `1px solid ${theme.palette.primary.border2}`,
        borderRadius: "12px",
        boxShadow: theme.shadows[1],
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {title && (
        <Typography variant="h6" fontWeight="600" gutterBottom>
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {subtitle}
        </Typography>
      )}
      <TableContainer sx={{ maxHeight, overflowX: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.field}
                  align={column.align || "left"}
                  sx={{
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    bgcolor: theme.palette.background.default,
                    width: column.width || "auto",
                  }}
                >
                  {column.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow
                  key={row.id ?? index}
                  sx={{ "&:hover": { bgcolor: theme.palette.action.hover } }}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.field}
                      align={column.align || "left"}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {column.renderCell
                        ? column.renderCell(row)
                        : (row[column.field] ?? "-")}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ReportTable;
